import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import SuccessMessage from '../common/successmessage';
import GeneralButton from '../common/button_general';
import Spinner from '../common/spinner';
import { getHeaders, handleErrorResponse } from '../../lib/requests';

function MenuImport() {
  const [importText, setImportText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    setShowSuccess(false);
    setErrorMessage('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const headers = getHeaders(apiToken);
      headers['Content-Type'] = 'text/plain';
      await axios.post(`${apiUrl}/menus/import`, importText, { headers });
      setSuccessMessage('Menu selections imported successfully');
      setShowSuccess(true);
      setImportText('');
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => navigate('/menus');

  return (
    <MasterLayout pageTitle="Import Menu Selections">
      {showError && <ErrorMessage message={errorMessage} />}
      {showSuccess && <SuccessMessage message={successMessage} />}
      {isLoading && <Spinner />}
      {!isLoading && (
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <label htmlFor="importText" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              Paste tab-delimited data (customer_id, slot_number, menu_id, item_id, quantity)
            </label>
            <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium mb-2">Import Instructions:</p>
              <ul className="list-disc list-inside space-y-1 text-xs text-blue-700">
                <li><strong>customer_id:</strong> The customer UUID (export from Clients page)</li>
                <li><strong>slot_number:</strong> The default order slot number (1, 2, 3, or 4)</li>
                <li><strong>menu_id:</strong> The menu UUID (export from Menus page)</li>
                <li><strong>item_id:</strong> The menu item UUID (export from Items page)</li>
                <li><strong>quantity:</strong> Number of items for this selection</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2 font-medium">
                Tip: Use the &quot;Export&quot; buttons on the Clients, Items, and Menus pages to get the UUIDs you need.
              </p>
            </div>
            <textarea
              id="importText"
              name="importText"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={10}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 font-mono dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="a1b2c3d4-e5f6-7890-abcd-ef1234567890	1	g8h9i0j1-k2l3-4567-89mn-op1234567890	f9e8d7c6-b5a4-3210-9876-543210fedcba	2"
              required
            />
            <div className="mt-2 text-xs text-gray-600">
              <p className="font-medium">Example format:</p>
              <code className="block bg-gray-100 p-2 rounded mt-1 font-mono">
                a1b2c3d4-e5f6-7890-abcd-ef1234567890	1	g8h9i0j1-k2l3-4567-89mn-op1234567890	f9e8d7c6-b5a4-3210-9876-543210fedcba	2
              </code>
              <p className="mt-1">This creates a Default Order slot 1 with quantity 2 of that menu item for that customer using the specified menu.</p>
            </div>
          </div>
          <div className="flex justify-center mt-6 gap-2">
            <GeneralButton type="submit" buttonType="primary">
              Import
            </GeneralButton>
            <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
              Back to Menu List
            </GeneralButton>
          </div>
        </form>
      )}
    </MasterLayout>
  );
}

export default MenuImport;
